/**
 * Drilldown Header
 * Container for common record drilldown header items such as
 * title and back button
 */
import type { IconName } from '@fortawesome/fontawesome-svg-core';
import { Button, Grid2, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import DotMenu from '@components/DotMenu';
import type { DotMenuAction } from '@components/DotMenu/DotMenu';
import Icon from '@components/Icon/Icon';
import IconButton from '@components/IconButton';
import RecordsBackButton from '@components/RecordsBackButton';

import { useIsMobile } from '@hooks/useIsMobile';

import {
  AdditionalInfo,
  ChildDiv,
  HeaderContainer,
  HeaderContent,
  Item,
  ItemIcon,
  ItemTitle,
  ItemTitleWrapper,
  MobileBackButton,
  OuterContainer,
  Subtitle,
  Title,
  TitleDiv,
} from './DrilldownHeader.style';
import HeaderArc from './HeaderArc';

const messages = defineMessages({
  back: {
    id: 'GLOBAL.back',
    defaultMessage: 'Back',
  },
});

type HeaderItems = {
  title?: React.ReactNode;
  iconName?: IconName;
  iconColor?: string;
};

type DrilldownHeaderProps = {
  actions?: DotMenuAction[];
  children?: React.ReactNode;
  items?: HeaderItems[];
  showBackground?: boolean;
  subtitle?: React.ReactNode | string[] | string | null;
  subtitleSize?: string;
  title?: React.ReactNode | null;
  heading?: React.ReactNode | null;
  loading?: boolean;
  /** Show subtitle even if there isn't any text */
  forceShowSubtitle?: boolean;
  /** Show additional info items even if there isn't any content */
  forceShowItems?: boolean;
  preChildren?: React.ReactNode;
  buttonsElement?: React.ReactNode;
  /* 2 ways to redirect user to another page by clicking top-left back button*/
  // 1. Pass in route as a string to redirect user
  backRoute?: string;
  // 2. Pass in callback function to redirect user
  onBackClick?: () => void;
};

function DrilldownHeader({
  title = '',
  heading = '',
  subtitle = '',
  items = [],
  backRoute = '',
  subtitleSize = '15px',
  showBackground = true,
  actions = [],
  children = null,
  loading = false,
  forceShowSubtitle = false,
  forceShowItems = false,
  preChildren,
  buttonsElement,
  onBackClick,
}: DrilldownHeaderProps) {
  const { formatMessage } = useIntl();
  const mobile = useIsMobile();
  const theme = useTheme();

  const subtitleElement = React.useMemo(() => {
    let _subtitle = subtitle;
    if (typeof _subtitle === 'string') {
      _subtitle = [_subtitle];
    }
    if (
      _subtitle instanceof Array &&
      _subtitle.filter((text) => text || forceShowSubtitle).length > 0
    ) {
      return (
        <Subtitle subtitleSize={subtitleSize}>
          {_subtitle
            .filter((text) => text || forceShowSubtitle)
            .reduce((acc, curr) => `${acc} • ${curr}`)}
        </Subtitle>
      );
    } else if (_subtitle) {
      return (
        <div
          style={{
            width: 'initial',
            color: 'initial',
            fontSize: 'initial',
          }}
        >
          {_subtitle}
        </div>
      );
    }
    return null;
  }, [forceShowSubtitle, subtitle, subtitleSize]);

  const displayBackButton = () => {
    if (onBackClick) {
      return mobile ? (
        <Button
          onClick={onBackClick}
          startIcon={
            <Icon
              clickable
              color="primary.contrastText"
              name="chevron-down"
              rotation={90}
              style={{ marginRight: 2 }}
              type="far"
            />
          }
          sx={{
            width: 'fit-content',
            fontSize: 16,
            opacity: 0.7,
            textTransform: 'capitalize',
            color: ({ palette }) => palette.primary.contrastText,
            backgroundColor: 'transparent',
            '&:hover': {
              opacity: 1,
            },
          }}
          variant="text"
        >
          {formatMessage(messages.back)}
        </Button>
      ) : (
        <IconButton
          color="primary.contrastText"
          iconButtonProps={{ edge: undefined }}
          iconType="far"
          name="chevron-down"
          onClick={onBackClick}
          rotation={90}
          sx={{
            fontSize: 30,
            opacity: 0.7,
            border: `1px solid ${theme.palette.divider}88`,
            '&:hover': {
              opacity: 1,
            },
          }}
        />
      );
    }
    if (backRoute) {
      return mobile ? (
        <MobileBackButton
          onClick={(e) => {
            e.preventDefault();
            if (window.history.length > 1) {
              window.history.back();
            } else {
              navigate(backRoute);
            }
          }}
          to={backRoute}
        >
          <Icon
            clickable
            color="primary.contrastText"
            name="chevron-down"
            rotation={90}
            style={{ marginRight: 2 }}
            type="far"
          />
          {formatMessage(messages.back)}
        </MobileBackButton>
      ) : (
        <RecordsBackButton linkTo={backRoute} />
      );
    }
    return null;
  };

  return (
    <OuterContainer>
      {/* {showBackground ? <BigCircle mobileHeight={headerHeight} /> : null} */}
      <HeaderContainer showBackground={showBackground}>
        <ContentLoader loading={loading}>
          <Grid2 container spacing={1} sx={{ padding: 2, width: '100%' }}>
            <Grid2 size={{ xs: 6, sm: 1 }} order={{ xs: 1, sm: 1 }}>
              {displayBackButton()}
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 10 }} order={{ xs: 3, sm: 2 }}>
              <TitleDiv>
                <HeaderContent>
                  <Typography
                    variant="h3"
                    color="primary.contrastText"
                    fontWeight="bold"
                    gutterBottom
                  >
                    {heading}
                  </Typography>
                  <Title>{title}</Title>
                  {subtitleElement}
                  {items instanceof Array && items.length > 0 ? (
                    <AdditionalInfo>
                      {items
                        .filter(({ title }) => title || forceShowItems)
                        .map(({ title, iconName, iconColor }, index) => (
                          <Item key={`drilldown-header-item-${title}-${index}`}>
                            <ItemTitleWrapper>
                              {iconName ? (
                                <ItemIcon>
                                  <Icon
                                    color={iconColor ?? 'warning.main'}
                                    name={iconName}
                                    type="fas"
                                  />
                                </ItemIcon>
                              ) : null}
                              <ItemTitle>{title ?? null}</ItemTitle>
                            </ItemTitleWrapper>
                          </Item>
                        ))}
                    </AdditionalInfo>
                  ) : null}
                  <>{preChildren}</>
                  {children ? <ChildDiv>{children}</ChildDiv> : null}
                </HeaderContent>
              </TitleDiv>
            </Grid2>
            <Grid2 size={{ xs: 6, sm: 1 }} order={{ xs: 2, sm: 3 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {buttonsElement}
                {actions.length ? <DotMenu actions={[...actions]} /> : null}
              </Stack>
            </Grid2>
          </Grid2>
        </ContentLoader>
      </HeaderContainer>
      {showBackground && <HeaderArc />}
    </OuterContainer>
  );
}

export { DrilldownHeader };
export type { DrilldownHeaderProps, HeaderItems };
